
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";

const InvitationPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract access token and type from URL hash or query parameters
    const hashParams = new URLSearchParams(location.hash?.substring(1) || '');
    const queryParams = new URLSearchParams(location.search);
    
    const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
    const type = hashParams.get('type') || queryParams.get('type');
    
    if (!accessToken || type !== 'invite') {
      console.warn('Missing access token or wrong type for invitation');
      setError("Invalid or missing invitation token. Please request a new invitation.");
      return;
    }
    
    // Get user email from session
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error || !data.session) {
          console.error('Error getting session:', error);
          return;
        }
        
        if (data.session?.user?.email) {
          setEmail(data.session.user.email);
        }
      } catch (err) {
        console.error('Error checking session:', err);
      }
    };
    
    checkSession();
    
    // Log debug info
    console.log('Invitation page loaded with params:', {
      hasAccessToken: !!accessToken,
      type,
      hash: location.hash,
      search: location.search
    });
  }, [location]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!password) {
      setError("Please enter a new password");
      return;
    }
    
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Update the password
      const { error } = await supabase.auth.updateUser({ 
        password 
      });

      if (error) {
        console.error('Error setting password:', error);
        setError(error.message);
        setIsProcessing(false);
        return;
      }

      // Also update first and last name if provided
      if (firstName || lastName) {
        const { error: profileError } = await supabase.auth.updateUser({
          data: {
            first_name: firstName,
            last_name: lastName
          }
        });
        
        if (profileError) {
          console.warn('Error updating profile data:', profileError);
          // Non-critical error, we can still continue
        }
      }

      // Password set successful
      setIsSuccess(true);
      toast.success("Your account has been set up successfully!");
      
      // Redirect to dashboard after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (error: any) {
      console.error('Exception during password setup:', error);
      setError(error.message || "An unexpected error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-md animate-fade-in">
          <h1 className="text-2xl font-semibold text-center mb-6">Complete Your Account Setup</h1>
          
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {isSuccess ? (
            <Alert className="mb-6 border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle>Account Setup Complete</AlertTitle>
              <AlertDescription>
                Your account has been set up successfully. You will be redirected to the dashboard shortly.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSetPassword} className="space-y-6">
              {email && (
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <div className="px-3 py-2 rounded-md border bg-muted/50">
                    {email}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a secure password"
                    disabled={isProcessing}
                    className="w-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    disabled={isProcessing}
                    className="w-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Complete Setup"}
              </Button>
            </form>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default InvitationPage;
